import { useEffect, useState, useCallback } from 'react'
import { useAuth } from '../lib/AuthContext'
import { useLang } from '../lib/LangContext'
import { getProduits, getBilanFinancier } from '../lib/api'

const CAT_EMOJI = { alimentation: '🍚', boissons: '🥤', hygiene: '🧴', epices: '🌶', conserves: '🥫', autres: '📦' }

// ── SEUILS DE PERTINENCE ──────────────────────────────────
const VENTES_MIN_PAR_SEMAINE = 1    // minimum 1 vente/semaine pour conseiller
const JOURS_MIN_DONNEES = 7         // minimum 7 jours de données
const MARGE_MIN_CONSEIL = 0         // bénéfice > 0 obligatoire

function calculerPrevisions(produits, mouvements) {
  const now = Date.now()
  const periode = 30

  return produits.map(p => {
    const sorties = mouvements.filter(m =>
      m.type === 'sortie' && m.produit_id === p.id &&
      (now - new Date(m.created_at).getTime()) < periode * 86400000
    )
    const entrees = mouvements.filter(m =>
      m.type === 'entree' && m.produit_id === p.id &&
      (now - new Date(m.created_at).getTime()) < periode * 86400000
    )

    const totalSorti = sorties.reduce((s, m) => s + m.quantite, 0)
    const vitesseParJour = totalSorti / periode
    const vitesseParSemaine = vitesseParJour * 7

    // Nombre de jours depuis le premier mouvement
    const tousMovs = [...sorties, ...entrees]
    const premierMouv = tousMovs.length > 0
      ? Math.min(...tousMovs.map(m => new Date(m.created_at).getTime()))
      : now
    const joursDepuisDebut = Math.ceil((now - premierMouv) / 86400000)

    const beneficeParUnite = (p.prix_unitaire || 0) - (p.prix_achat || 0)
    const beneficeSemaine = vitesseParSemaine * beneficeParUnite

    // Stock conseillé = 2 semaines de vente
    const stockConseille = Math.ceil(vitesseParSemaine * 2)

    // Urgence
    let joursAvantRupture = Infinity
    let urgence = 'ok'

    if (vitesseParJour > 0) {
      joursAvantRupture = Math.floor(p.quantite / vitesseParJour)
      if (p.quantite === 0) urgence = 'critique'
      else if (joursAvantRupture <= 3) urgence = 'critique'
      else if (joursAvantRupture <= 7) urgence = 'urgent'
      else if (joursAvantRupture <= 14) urgence = 'watch'
    } else if (p.quantite === 0) {
      urgence = 'critique'
    }

    return {
      ...p,
      vitesseParJour,
      vitesseParSemaine,
      joursAvantRupture,
      stockConseille,
      urgence,
      totalSorti30j: totalSorti,
      beneficeParUnite,
      beneficeSemaine,
      joursDepuisDebut,
      // Fiabilité du conseil
      donneesSuffisantes: joursDepuisDebut >= JOURS_MIN_DONNEES && vitesseParSemaine >= VENTES_MIN_PAR_SEMAINE
    }
  }).sort((a, b) => {
    const ordre = { critique: 0, urgent: 1, watch: 2, ok: 3 }
    return ordre[a.urgence] - ordre[b.urgence]
  })
}

function genererConseils(previsions, lang) {
  const conseils = []

  // FILTRE STRICT : uniquement produits avec données suffisantes
  const produitsValides = previsions.filter(p =>
    p.donneesSuffisantes &&           // assez de données
    p.beneficeParUnite > MARGE_MIN_CONSEIL &&  // bénéfice positif
    p.vitesseParSemaine >= VENTES_MIN_PAR_SEMAINE  // au moins 1 vente/semaine
  )

  // TOP 3 par bénéfice hebdomadaire
  const opportunites = [...produitsValides]
    .sort((a, b) => b.beneficeSemaine - a.beneficeSemaine)
    .slice(0, 3)

  opportunites.forEach(p => {
    // Stock recommandé = 2 semaines de vente (arrondi au sup)
    const stockRecommande = Math.max(
      Math.ceil(p.vitesseParSemaine * 2),
      p.quantite + Math.ceil(p.vitesseParSemaine)
    )

    // Gain = stock supplémentaire × vitesse de vente × bénéfice/unité
    const stockSupp = stockRecommande - p.quantite
    const gainEstime = Math.round(stockSupp * Math.min(p.vitesseParSemaine / stockSupp, 1) * p.beneficeParUnite * 7)

    if (gainEstime <= 0 || stockRecommande <= p.quantite) return

    conseils.push({
      type: 'opportunite',
      icon: '📈',
      titre: lang === 'fr'
        ? `Augmente ton stock de ${p.nom}`
        : `Increase your stock of ${p.nom}`,
      texte: lang === 'fr'
        ? `Ce produit se vend ${p.vitesseParSemaine.toFixed(1)} ${p.unite}/semaine et te rapporte ${p.beneficeParUnite.toLocaleString()} FCFA de bénéfice par ${p.unite}. Passer de ${p.quantite} à ${stockRecommande} ${p.unite} pourrait t'apporter ~${gainEstime.toLocaleString()} FCFA de bénéfice supplémentaire cette semaine.`
        : `This product sells ${p.vitesseParSemaine.toFixed(1)} ${p.unite}/week and earns you ${p.beneficeParUnite.toLocaleString()} FCFA profit per ${p.unite}. Going from ${p.quantite} to ${stockRecommande} ${p.unite} could bring ~${gainEstime.toLocaleString()} FCFA more profit this week.`,
      gain: gainEstime,
      produit: p,
      fiabilite: Math.min(100, Math.round((p.joursDepuisDebut / 30) * 100))
    })
  })

  // Urgents rentables
  const urgents = previsions.filter(p =>
    p.urgence === 'critique' &&
    p.beneficeParUnite > 0 &&
    p.donneesSuffisantes
  )
  urgents.forEach(p => {
    conseils.push({
      type: 'urgence',
      icon: '🚨',
      titre: lang === 'fr' ? `Commande ${p.nom} d'urgence !` : `Order ${p.nom} urgently!`,
      texte: lang === 'fr'
        ? `Rupture imminente ! Ce produit te rapporte ${p.beneficeParUnite.toLocaleString()} FCFA par ${p.unite}. Commande au moins ${Math.ceil(p.vitesseParSemaine * 2)} ${p.unite} pour 2 semaines.`
        : `Imminent stockout! This product earns you ${p.beneficeParUnite.toLocaleString()} FCFA per ${p.unite}. Order at least ${Math.ceil(p.vitesseParSemaine * 2)} ${p.unite} for 2 weeks.`,
      gain: null,
      produit: p,
      fiabilite: Math.min(100, Math.round((p.joursDepuisDebut / 30) * 100))
    })
  })

  // Mauvaise marge
  const mauvaiseMarge = previsions.filter(p =>
    p.donneesSuffisantes &&
    p.vitesseParSemaine >= VENTES_MIN_PAR_SEMAINE &&
    p.prix_unitaire > 0 &&
    p.beneficeParUnite > 0 &&
    (p.beneficeParUnite / p.prix_unitaire) < 0.10
  )
  mauvaiseMarge.slice(0, 1).forEach(p => {
    const prixConseille = Math.ceil(p.prix_achat * 1.2)
    conseils.push({
      type: 'prix',
      icon: '💡',
      titre: lang === 'fr' ? `Vérifie le prix de ${p.nom}` : `Check the price of ${p.nom}`,
      texte: lang === 'fr'
        ? `Ta marge est seulement de ${Math.round((p.beneficeParUnite / p.prix_unitaire) * 100)}% sur ce produit. Un prix de ${prixConseille.toLocaleString()} FCFA donnerait une marge de 20%, soit +${(prixConseille - p.prix_unitaire).toLocaleString()} FCFA de bénéfice supplémentaire par ${p.unite}.`
        : `Your margin is only ${Math.round((p.beneficeParUnite / p.prix_unitaire) * 100)}% on this product. A price of ${prixConseille.toLocaleString()} FCFA would give a 20% margin.`,
      gain: null,
      produit: p,
      fiabilite: Math.min(100, Math.round((p.joursDepuisDebut / 30) * 100))
    })
  })

  return conseils
}

const URGENCE_CONFIG = {
  critique: { color: 'var(--rose)', bg: 'var(--rose-pale)', border: '#DFA0A0', label_fr: 'Critique', label_en: 'Critical', icon: '🚨' },
  urgent:   { color: '#C4873A', bg: 'var(--amber-pale)', border: '#E8C080', label_fr: 'Urgent', label_en: 'Urgent', icon: '⚠️' },
  watch:    { color: 'var(--muted)', bg: '#F0F4F2', border: '#C5D4CC', label_fr: 'À surveiller', label_en: 'Watch', icon: '👁' },
  ok:       { color: 'var(--sage)', bg: 'var(--sage-pale)', border: 'var(--sage-light)', label_fr: 'OK', label_en: 'OK', icon: '✅' },
}

// Barre de fiabilité
function BarreFiabilite({ pct, lang }) {
  const color = pct < 30 ? 'var(--rose)' : pct < 70 ? 'var(--amber)' : 'var(--sage)'
  const label = pct < 30
    ? (lang === 'fr' ? 'Peu fiable — manque de données' : 'Unreliable — not enough data')
    : pct < 70
    ? (lang === 'fr' ? 'Moyennement fiable' : 'Moderately reliable')
    : (lang === 'fr' ? 'Fiable' : 'Reliable')
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
        <div style={{ fontSize: 10, color: 'var(--muted)' }}>
          {lang === 'fr' ? 'Fiabilité du conseil' : 'Tip reliability'}
        </div>
        <div style={{ fontSize: 10, fontWeight: 600, color }}>{label} ({pct}%)</div>
      </div>
      <div style={{ height: 4, background: 'var(--cream-dark)', borderRadius: 2 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 2, transition: 'width 0.5s' }} />
      </div>
    </div>
  )
}

export default function Previsions({ boutiqueId: boutiqueIdProp }) {
  const { boutiqueId: boutiqueIdAuth } = useAuth()
  const { lang } = useLang()
  const boutiqueId = boutiqueIdProp || boutiqueIdAuth

  const [previsions, setPrevisions] = useState([])
  const [conseils, setConseils] = useState([])
  const [loading, setLoading] = useState(true)
  const [onglet, setOnglet] = useState('alertes')

  const load = useCallback(async () => {
    if (!boutiqueId) return
    setLoading(true)
    const [{ data: prods }, { data: movs }] = await Promise.all([
      getProduits(boutiqueId),
      getBilanFinancier(boutiqueId, 30)
    ])
    const calc = calculerPrevisions(prods || [], movs || [])
    setPrevisions(calc)
    setConseils(genererConseils(calc, lang))
    setLoading(false)
  }, [boutiqueId, lang])

  useEffect(() => { load() }, [load])

  const critiques = previsions.filter(p => p.urgence === 'critique').length
  const urgents = previsions.filter(p => p.urgence === 'urgent').length
  const alertesProds = previsions.filter(p => p.urgence !== 'ok')

  // Produits sans données suffisantes
  const sansDonnees = previsions.filter(p =>
    !p.donneesSuffisantes && p.quantite > 0
  ).length

  return (
    <div className="main">
      <div className="page-title">{lang === 'fr' ? 'Prévisions' : 'Forecasts'}</div>

      {/* ONGLETS */}
      <div className="filter-tabs" style={{ marginBottom: '1rem' }}>
        <button className={`filter-tab ${onglet === 'alertes' ? 'active' : ''}`} onClick={() => setOnglet('alertes')}>
          {lang === 'fr' ? `⚠ Alertes (${alertesProds.length})` : `⚠ Alerts (${alertesProds.length})`}
        </button>
        <button className={`filter-tab ${onglet === 'conseils' ? 'active' : ''}`} onClick={() => setOnglet('conseils')}>
          {lang === 'fr' ? `💡 Conseils (${conseils.length})` : `💡 Tips (${conseils.length})`}
        </button>
        <button className={`filter-tab ${onglet === 'tous' ? 'active' : ''}`} onClick={() => setOnglet('tous')}>
          {lang === 'fr' ? 'Tous' : 'All'}
        </button>
      </div>

      {loading ? (
        <div className="empty">{lang === 'fr' ? 'Analyse en cours...' : 'Analyzing...'}</div>
      ) : (
        <>
          {/* ── ALERTES ── */}
          {onglet === 'alertes' && (
            <>
              {(critiques > 0 || urgents > 0) && (
                <div style={{ background: critiques > 0 ? 'var(--rose-pale)' : 'var(--amber-pale)', border: `1.5px solid ${critiques > 0 ? '#DFA0A0' : '#E8C080'}`, borderRadius: 'var(--radius-lg)', padding: '1rem 1.25rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ fontSize: 32 }}>{critiques > 0 ? '🚨' : '⚠️'}</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--charcoal)', marginBottom: 2 }}>
                      {lang === 'fr' ? `${critiques + urgents} produit(s) à traiter` : `${critiques + urgents} product(s) to handle`}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {critiques} {lang === 'fr' ? 'critique(s)' : 'critical'} · {urgents} {lang === 'fr' ? 'urgent(s)' : 'urgent'}
                    </div>
                  </div>
                </div>
              )}

              {alertesProds.length === 0 ? (
                <div className="empty">
                  <span className="empty-icon">✅</span>
                  {lang === 'fr' ? 'Aucune alerte — tout est en ordre !' : 'No alerts — everything is fine!'}
                </div>
              ) : alertesProds.map(p => {
                const cfg = URGENCE_CONFIG[p.urgence]
                return (
                  <div key={p.id} style={{ background: 'var(--warm-white)', border: `1.5px solid ${cfg.border}`, borderRadius: 'var(--radius-lg)', padding: '14px', marginBottom: 10, boxShadow: 'var(--shadow-sm)' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <div style={{ width: 44, height: 44, borderRadius: 'var(--radius-md)', background: cfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
                        {CAT_EMOJI[p.categorie] || '📦'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                          <div style={{ fontSize: 14, fontWeight: 600 }}>{p.nom}</div>
                          <span style={{ padding: '2px 9px', borderRadius: 50, fontSize: 10, fontWeight: 600, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>
                            {cfg.icon} {lang === 'fr' ? cfg.label_fr : cfg.label_en}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 6 }}>
                          <div>
                            <div style={{ fontSize: 10, color: 'var(--muted)' }}>{lang === 'fr' ? 'Stock actuel' : 'Current stock'}</div>
                            <div style={{ fontFamily: 'Fraunces, serif', fontSize: 20, color: cfg.color }}>{p.quantite} <span style={{ fontSize: 11, color: 'var(--muted)' }}>{p.unite}</span></div>
                          </div>
                          {p.joursAvantRupture !== Infinity && p.vitesseParJour > 0 && (
                            <div>
                              <div style={{ fontSize: 10, color: 'var(--muted)' }}>{lang === 'fr' ? 'Rupture dans' : 'Stockout in'}</div>
                              <div style={{ fontFamily: 'Fraunces, serif', fontSize: 20, color: cfg.color }}>
                                {p.joursAvantRupture === 0 ? (lang === 'fr' ? 'Épuisé' : 'Out') : `${p.joursAvantRupture}j`}
                              </div>
                            </div>
                          )}
                          {p.beneficeParUnite > 0 && (
                            <div>
                              <div style={{ fontSize: 10, color: 'var(--muted)' }}>{lang === 'fr' ? 'Bénéfice/unité' : 'Profit/unit'}</div>
                              <div style={{ fontFamily: 'Fraunces, serif', fontSize: 20, color: 'var(--sage)' }}>+{p.beneficeParUnite.toLocaleString()}</div>
                            </div>
                          )}
                        </div>
                        {p.stockConseille > 0 && p.vitesseParJour > 0 && (
                          <div style={{ background: cfg.bg, borderRadius: 8, padding: '7px 10px', fontSize: 12, color: cfg.color, fontWeight: 500 }}>
                            💡 {lang === 'fr' ? `Commander ~${p.stockConseille} ${p.unite} (2 semaines)` : `Order ~${p.stockConseille} ${p.unite} (2 weeks)`}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </>
          )}

          {/* ── CONSEILS ── */}
          {onglet === 'conseils' && (
            <>
              {/* Avertissement données insuffisantes */}
              {sansDonnees > 0 && (
                <div style={{ background: 'var(--amber-pale)', border: '1px solid #E8C080', borderRadius: 'var(--radius-md)', padding: '10px 14px', marginBottom: '1rem', fontSize: 12, color: '#633806', lineHeight: 1.7 }}>
                  ℹ️ {lang === 'fr'
                    ? `${sansDonnees} produit(s) ont trop peu de données pour générer des conseils fiables. Continuez à enregistrer vos ventes pendant au moins 1 semaine.`
                    : `${sansDonnees} product(s) have too little data for reliable tips. Keep recording sales for at least 1 week.`}
                </div>
              )}

              <div style={{ background: 'var(--sage-pale)', border: '1px solid var(--sage-light)', borderRadius: 'var(--radius-md)', padding: '10px 14px', marginBottom: '1rem', fontSize: 12, color: 'var(--sage-dark)', lineHeight: 1.7 }}>
                💡 {lang === 'fr'
                  ? 'Conseils basés sur tes 30 derniers jours — vitesse de vente + bénéfice par produit. Minimum 1 vente/semaine requis.'
                  : 'Tips based on your last 30 days — sales speed + profit per product. Minimum 1 sale/week required.'}
              </div>

              {conseils.length === 0 ? (
                <div className="empty">
                  <span className="empty-icon">📊</span>
                  {lang === 'fr'
                    ? 'Pas encore assez de données. Enregistre tes ventes régulièrement pendant au moins 1 semaine.'
                    : 'Not enough data yet. Record your sales regularly for at least 1 week.'}
                </div>
              ) : conseils.map((c, i) => (
                <div key={i} style={{
                  background: 'var(--warm-white)',
                  border: `1.5px solid ${c.type === 'urgence' ? '#DFA0A0' : c.type === 'opportunite' ? 'var(--sage-light)' : '#E8C080'}`,
                  borderRadius: 'var(--radius-lg)', padding: '14px 16px', marginBottom: 12, boxShadow: 'var(--shadow-sm)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ fontSize: 28, flexShrink: 0 }}>{c.icon}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--charcoal)', marginBottom: 6 }}>{c.titre}</div>
                      <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.7 }}>{c.texte}</div>
                      {c.gain > 0 && (
                        <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--sage-pale)', border: '1px solid var(--sage-light)', borderRadius: 50, padding: '4px 12px' }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--sage-dark)' }}>
                            +{c.gain.toLocaleString()} FCFA {lang === 'fr' ? 'de gain potentiel/semaine' : 'potential gain/week'}
                          </span>
                        </div>
                      )}
                      {/* BARRE DE FIABILITÉ */}
                      <BarreFiabilite pct={c.fiabilite} lang={lang} />
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* ── TOUS LES PRODUITS ── */}
          {onglet === 'tous' && previsions.map(p => {
            const cfg = URGENCE_CONFIG[p.urgence]
            return (
              <div key={p.id} style={{ background: 'var(--warm-white)', border: '1px solid var(--cream-dark)', borderRadius: 'var(--radius-md)', padding: '10px 14px', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 18 }}>{CAT_EMOJI[p.categorie] || '📦'}</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{p.nom}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                      {p.vitesseParSemaine >= VENTES_MIN_PAR_SEMAINE
                        ? `${p.vitesseParSemaine.toFixed(1)} ${p.unite}/sem${p.beneficeParUnite > 0 ? ` · +${p.beneficeParUnite.toLocaleString()} FCFA/${p.unite}` : ''}`
                        : (lang === 'fr' ? 'Pas assez de ventes pour prévoir' : 'Not enough sales to forecast')}
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: 'Fraunces, serif', fontSize: 18, color: cfg.color }}>{p.quantite}</div>
                  <div style={{ fontSize: 10, color: 'var(--muted)' }}>{p.unite}</div>
                </div>
              </div>
            )
          })}
        </>
      )}
    </div>
  )
}
